import { memo } from "react"
import { useNavigate } from "@tanstack/react-router"
import {
	IconCircleArrowUpOutline18 as ArrowUpCircle,
	IconAtom2Outline18 as Atom,
	IconCircleInfoOutline18 as Info,
} from "nucleo-ui-outline-18"

import { pro } from "@productlane/lib/src/plans-client"
import { Button, Tooltip } from "@productlane/ui"

import { toast } from "@/components/toast"
import { useRequiredPlans } from "@/lib/plans"

interface GenerateChangelogButtonProps {
	onClick: () => void
	disabled?: boolean
	isLoading?: boolean
	loadingText?: string
	tooltipLabel?: string
	workspaceLinearConnected?: boolean
	showHowItWorks?: boolean
	onHowItWorksClick?: () => void
	variant?: "default" | "outline" | "ghost" | "cta"
	label?: string
}

export const GenerateChangelogButton = memo(
	({
		onClick,
		disabled = false,
		isLoading = false,
		loadingText = "",
		tooltipLabel = "Generate a Changelog with all issues and projects since your last release.",
		workspaceLinearConnected = true,
		showHowItWorks = true,
		onHowItWorksClick,
		variant = "cta",
		label = "Generate Changelog",
	}: GenerateChangelogButtonProps) => {
		const navigate = useNavigate()
		const { passes: hasRequiredPlan } = useRequiredPlans(pro.andUp)
		const handleHowItWorksClick = () => {
			if (onHowItWorksClick) {
				onHowItWorksClick()
			} else {
				// @ts-expect-error Global widget calls
				// eslint-disable-next-line
				window.Productlane.openDocs("90840b98-ee11-4eb0-b7a0-c24c8196c646")
			}
		}

		return (
			<div className="flex items-center gap-3">
				{/* Outside the tooltip, which hands its props to a single child. */}
				<style>
					{`
					@keyframes shimmer {
						0% { background-position: -200% 0; }
						100% { background-position: 200% 0; }
					}
				`}
				</style>
				<Tooltip
					label={
						hasRequiredPlan
							? tooltipLabel
							: "Upgrade to Pro or higher to unlock AI Changelog generation"
					}
					enabled={!isLoading}
				>
					<Button
						disabled={disabled}
						Icon={hasRequiredPlan ? Atom : ArrowUpCircle}
						iconClassName={
							hasRequiredPlan
								? variant === "cta"
									? "text-white"
									: "text-primary"
								: undefined
						}
						onClick={() => {
							if (!workspaceLinearConnected) {
								toast.info({
									title: "Linear is not connected",
									icon: Info,
									descriptionAction: {
										label: "Click here to connect Linear in the settings",
										callback: () => {
											void navigate({ to: "/settings/integrations/linear" })
										},
									},
								})
								return
							}

							if (hasRequiredPlan) {
								onClick()
							} else {
								toast.info({
									title:
										"Release intelligence is available on Pro and Scale plans",
									action: {
										label: "View Plans",
										callback: () => {
											void navigate({ to: "/settings/plans" })
										},
									},
								})
							}
						}}
						className="relative"
						variant={variant}
					>
						<span
							className="relative z-10"
							style={
								isLoading
									? {
											background:
												"linear-gradient(90deg, #6b7280 25%, #e5e7eb 50%, #6b7280 75%)",
											backgroundSize: "200% 100%",
											backgroundClip: "text",
											WebkitBackgroundClip: "text",
											color: "transparent",
											animation: "shimmer 3.1s linear infinite",
										}
									: undefined
							}
						>
							{isLoading && loadingText ? loadingText : label}
						</span>
					</Button>
				</Tooltip>
				{showHowItWorks && (
					<Button variant="outline" onClick={handleHowItWorksClick}>
						How it works
					</Button>
				)}
			</div>
		)
	},
)
